import React, { useState, useEffect, useRef } from 'react';
import SecureSessionService from '../../services/session/SecureSessionService';
import { SESSION_ERROR_TYPES, getErrorMessage } from '../../constants/sessionErrors';
import { ORIGINAL_CLIENT_REDIRECT_URL } from '../../config';
import PageLayout from '../../components/PageLayout/CarelonRx';
import { useSession } from '../../contexts/SessionContext';
import { useFlow } from '../../contexts/FlowContext';
import { useApiError } from '../../hooks/useApiError';
import ApiErrorBanner from '../modals/ApiErrorBanner';
import SessionExpiryModal from '../modals/SessionExpiryModal';
import '../../styles/CarelonRx/sessionValidator.css';

interface SecureSessionValidatorProps {
  children: React.ReactNode;
}

const SecureSessionValidator: React.FC<SecureSessionValidatorProps> = ({ children }) => {
  const [isValidating, setIsValidating] = useState(true);
  const [sessionError, setSessionError] = useState('');
  const { sessionData, setSessionData, setForceSessionExpired } = useSession();
  const { setFlowData } = useFlow();
  const { errorState, showError, hideError } = useApiError();
  const hasValidated = useRef(false);


  useEffect(() => {
    if (hasValidated.current) return;
    hasValidated.current = true;
    validateNonceAndCreateSession();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      SecureSessionService.clearSession();
      
      // TODO: Use RelayState URL from sessionStorage if available, fallback to env variable
      if (ORIGINAL_CLIENT_REDIRECT_URL) {
        window.location.replace(ORIGINAL_CLIENT_REDIRECT_URL);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);


  const validateNonceAndCreateSession = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('sessionId') || '';
      const nonce = urlParams.get('nonce') || '';
      const userId = urlParams.get('userId') || '';
      const device = urlParams.get('device') || '';

      if (device) {
        setFlowData('device', device);
      }

      if (!sessionId && !nonce) {
        const storedSession = SecureSessionService.getStoredSession();
        if (storedSession) {
          setSessionData(storedSession);
          setIsValidating(false);
          return;
        }
        
        const errorConfig = getErrorMessage(SESSION_ERROR_TYPES.MISSING_PARAMS);
        setSessionError(errorConfig.message);
        setIsValidating(false);
        return;
      }

      if (!sessionId || !nonce) {
        const errorConfig = getErrorMessage(SESSION_ERROR_TYPES.MISSING_PARAMS);
        setSessionError(errorConfig.message);
        setIsValidating(false);
        return;
      }

      if (!SecureSessionService.isValidNonceFormat(nonce)) {
        const errorConfig = getErrorMessage(SESSION_ERROR_TYPES.INVALID_NONCE);
        setSessionError(errorConfig.message);
        setIsValidating(false);
        return;
      }

      const validation = await SecureSessionService.validateSessionAndNonce(sessionId, nonce);
      
      if (!validation.valid) {
        if (validation.errorType === SESSION_ERROR_TYPES.SESSION_EXPIRED) {
          // TODO: Use RelayState URL from sessionStorage if available, fallback to env variable
          window.location.href = ORIGINAL_CLIENT_REDIRECT_URL;
          return;
        }
        
        const errorConfig = getErrorMessage(validation.errorType || SESSION_ERROR_TYPES.GENERIC_ERROR);
        setSessionError(errorConfig.message);
        setIsValidating(false);
        return;
      }

      if (!validation.expiresAt || validation.expiresAt === 0) {
        setForceSessionExpired(true);
        setIsValidating(false);
        return;
      }

      const sessionInfo = {
        sessionId: sessionId,
        userId: userId,
        expiresAt: validation.expiresAt
      };

      SecureSessionService.storeSession(sessionInfo);
      setSessionData(sessionInfo);

      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('sessionId');
      cleanUrl.searchParams.delete('nonce');
      cleanUrl.searchParams.delete('userId');
      cleanUrl.searchParams.delete('device');
      window.history.replaceState({}, '', cleanUrl.toString());

      setIsValidating(false);

    } catch (error: any) {
      showError('Sorry, we are experiencing technical issues. Please give us time to fix the issues. Then, try again.');
      setIsValidating(false);
    }
  };


  const handleRedirect = () => {
    SecureSessionService.clearSession();
    
    window.location.replace(ORIGINAL_CLIENT_REDIRECT_URL);
  };

  const handleRetry = () => {
    hideError();
    setIsValidating(true);
    hasValidated.current = false;
    validateNonceAndCreateSession();
  };

  if (errorState.isVisible) {
    return (
      <ApiErrorBanner
        isVisible={errorState.isVisible}
        onRetry={handleRetry}
        showRetryButton={true}
        buttonText="Retry"
      />
    );
  }

  if (sessionError) {
    return (
      <PageLayout bannerTitle="">
        <div className="login-page-container">
          <div className="login-left-panel">
            <div className="modal-overlay" style={{ position: 'fixed' }}>
              <div className="modal-content">
                <h2 className="modal-title">Verification Error</h2>
                <p className="modal-message">
                  {sessionError}
                </p>
                <div className="modal-actions">
                  <button onClick={handleRedirect} className="modal-btn-stay">
                    Return to Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (sessionData) {
    return (
      <>
        {children}
        <SessionExpiryModal />
      </>
    );
  }

 return (
  <PageLayout bannerTitle="">
    <SessionExpiryModal />
  </PageLayout>
);
};

export default SecureSessionValidator;
